/**
 * Simple in-memory cache with TTL support and automatic cleanup
 */
interface CacheEntry<T> {
  data: T;
  expiresAt: number;
  lastAccessed: number;
}

/**
 * APICache class to manage in-memory cache
 */
class APICache {
  private cache = new Map<string, CacheEntry<unknown>>();
  private readonly defaultTTL = 10 * 60 * 1000; // 10 minutes
  private readonly maxEntries = 100; // Maximum number of cache entries
  private readonly cleanupInterval = 60 * 1000; // Cleanup every minute
  private cleanupTimer: NodeJS.Timeout | null = null;

  constructor() {
    // Start automatic cleanup
    this.startCleanup();
  }

  private startCleanup(): void {
    if (typeof window === 'undefined') return; // Don't run in SSR

    this.cleanupTimer = setInterval(() => {
      this.removeExpired();
    }, this.cleanupInterval);

    // Cleanup on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.stopCleanup());
    }
  }

  private stopCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  private removeExpired(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => this.cache.delete(key));

    // If still over limit, remove least recently accessed entries
    if (this.cache.size > this.maxEntries) {
      this.evictLRU();
    }
  }

  private evictLRU(): void {
    // Sort by last accessed time and remove oldest
    const entries = Array.from(this.cache.entries()).sort(
      (a, b) => a[1].lastAccessed - b[1].lastAccessed,
    );

    const toRemove = entries.length - this.maxEntries;
    for (let i = 0; i < toRemove; i++) {
      this.cache.delete(entries[i][0]);
    }
  }

  /**
   * Set a value in the cache
   *
   * @param key - The cache key
   * @param data - The data to cache
   * @param ttlMs - Optional time-to-live in milliseconds
   */
  set<T>(key: string, data: T, ttlMs?: number): void {
    const expiresAt = Date.now() + (ttlMs ?? this.defaultTTL);
    this.cache.set(key, {
      data,
      expiresAt,
      lastAccessed: Date.now(),
    });

    // Check if we need to evict
    if (this.cache.size > this.maxEntries) {
      this.evictLRU();
    }
  }

  /**
   * Get a value from the cache
   *
   * @param key - The cache key
   * @returns The cached data or null if not found or expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    // Update last accessed time
    entry.lastAccessed = Date.now();

    return entry.data as T;
  }

  /**
   * Check if a key exists in the cache and is not expired
   *
   * @param key - The cache key
   * @returns True if the key exists and is valid, false otherwise
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Clear the cache, optionally matching a pattern
   *
   * @param pattern - Optional regular expression to match keys to delete
   */
  clear(pattern?: RegExp): void {
    if (!pattern) {
      this.cache.clear();
      return;
    }

    // Clear entries matching pattern
    for (const key of this.cache.keys()) {
      if (pattern.test(key)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get the current size of the cache after removing expired entries
   *
   * @returns The number of items in the cache
   */
  size(): number {
    // Remove expired entries
    this.removeExpired();
    return this.cache.size;
  }

  /**
   * Get memory usage estimation in bytes
   *
   * @returns Estimated size in bytes
   */
  getMemoryUsage(): number {
    let size = 0;
    for (const [key, entry] of this.cache.entries()) {
      // Rough estimation: key size + JSON size of data + metadata
      size += key.length * 2; // 2 bytes per char
      size += JSON.stringify(entry.data).length * 2;
      size += 24; // metadata (timestamps)
    }
    return size;
  }
}

export const apiCache = new APICache();
